// src/pages/MatchSetup/index.js
//
// Captain-facing match setup page. Translates match-setup-mockup-v5
// into a real React component wired to /api/scorer endpoints.
//
// Flow:
//   1. (admin) Sets toss-1 winner → squad picker unlocks
//   2. Captains alternate-pick squads (server enforces turn + side)
//   3. (admin) Sets toss-2 winner + bat/bowl decision
//   4. (admin) Sets/edits match rules (overs, players/side, lms, saanjha)
//   5. (admin) Submits → match enters setup_done state, ready to start innings 1
//
// Roles:
//   - super_admin / admin: full access
//   - capt1 / capt2: can pick to their own side, remove from their own side
//   - any other logged-in member: read-only view
//
// Polling: see useSetupState.js (adaptive 3-10s).

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import useSetupState from './useSetupState';
import SidesGrid from './SidesGrid';
import UnassignedList from './UnassignedList';
import { AddMemberModal, ConditionsModal } from './Modals';
import './MatchSetup.css';

// ─── Pure helper: whose turn is it? ─────────────────────────
function whoseTurn(state, squadSize) {
  const cap = state?.captaincy;
  if (!cap?.squad_selection_winner_id) return null;
  const target = squadSize - 1;
  const aPicked = state.squad.filter(m => m.side === 'A' && !m.is_captain && !m.is_saanjha).length;
  const bPicked = state.squad.filter(m => m.side === 'B' && !m.is_captain && !m.is_saanjha).length;
  if (aPicked >= target && bPicked >= target) return null;
  if (aPicked >= target) return 'B';
  if (bPicked >= target) return 'A';
  const swSide = state.squad.find(m => m.member_id === cap.squad_selection_winner_id)?.side;
  if (!swSide) return null;
  const total = aPicked + bPicked;
  return (total % 2 === 0) ? swSide : (swSide === 'A' ? 'B' : 'A');
}

function fmtMatchDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short'
    });
  } catch {
    return d;
  }
}

// Build a labeler over a set of members. Always the FULL name — scorers and
// captains were confused by "Amit" / "A. Bansal" style short names when three
// Amits play. If two members have the very same full name, the UCC id is
// added so they can still be told apart.
function makeNameLabeler(members) {
  const freq = {};
  (members || []).forEach(m => {
    const k = (m && m.display_name || '').trim().toLowerCase();
    if (k) freq[k] = (freq[k] || 0) + 1;
  });
  return (m) => {
    const full = (m && m.display_name || '').trim();
    if (!full) return '';
    if ((freq[full.toLowerCase()] || 0) > 1 && m.ucc_id) return `${full} (${m.ucc_id})`;
    return full;
  };
}

// ═══════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════
export default function MatchSetupPage() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { member } = useAuth();
  const myMemberId = member?.id;

  const { state, error, refetch, loading } = useSetupState(matchId, myMemberId);

  // Strengths (loaded once per match)
  const [strengths, setStrengths] = useState(null);
  useEffect(() => {
    if (!matchId) return;
    api.get(`/scorer/matches/${matchId}/member-strengths`)
      .then(r => setStrengths(r.data))
      .catch(() => setStrengths({ member_strengths: [] }));
  }, [matchId]);

  const strengthsByMember = useMemo(() => {
    const m = new Map();
    (strengths?.member_strengths || []).forEach(s => m.set(s.member_id, s));
    return m;
  }, [strengths]);

  // Local UI state — admin-editable rules + team names. Synced to server
  // via POST /scorer/matches/:id/setup whenever values change (debounced).
  const [teamAName,    setTeamAName]    = useState('');
  const [teamBName,    setTeamBName]    = useState('');
  const [overs,        setOvers]        = useState(25);
  const [playersSide,  setPlayersSide]  = useState(11);
  const [lastManStands, setLMS]         = useState(true);
  const [saanjha,      setSaanjha]      = useState(false);
  const [minBowlers,   setMinBowlers]   = useState(null);   // null = no bowling limit

  // Sync local state from server on each refresh (only when fields exist)
  useEffect(() => {
    if (state?.captaincy?.capt1_team) setTeamAName(state.captaincy.capt1_team);
    if (state?.captaincy?.capt2_team) setTeamBName(state.captaincy.capt2_team);
    if (state?.match?.overs)            setOvers(state.match.overs);
    if (state?.match?.players_per_side) setPlayersSide(state.match.players_per_side);
    if (typeof state?.match?.last_man_stands === 'boolean') setLMS(state.match.last_man_stands);
    if (typeof state?.match?.saanjha_active  === 'boolean') setSaanjha(state.match.saanjha_active);
    if (state?.match) setMinBowlers(state.match.min_bowlers ?? state.match.min_bowlers_suggested ?? null);
  }, [state?.captaincy?.capt1_team, state?.captaincy?.capt2_team,
      state?.match?.overs, state?.match?.players_per_side,
      state?.match?.last_man_stands, state?.match?.saanjha_active, state?.match?.min_bowlers]);

  // Debounced rule save — fires 500ms after admin stops fiddling with chips.
  // Only admins can save (server enforces). Captains can't see this section anyway.
  useEffect(() => {
    if (!state || !state.match) return;
    if (!['super_admin', 'admin'].includes(member?.role)) return;
    // Only save if values differ from server state (avoid loop)
    const dirtyOvers = (state.match.overs !== overs);
    const dirtyPPS   = (state.match.players_per_side ?? 11) !== playersSide;
    const dirtyLMS   = (state.match.last_man_stands ?? true) !== lastManStands;
    const dirtySB    = (state.match.saanjha_active ?? false) !== saanjha;
    const dirtyMB    = (state.match.min_bowlers ?? null) !== (minBowlers ?? null);
    const dirtyTA    = (teamAName || '') !== (state.captaincy?.capt1_team || '');
    const dirtyTB    = (teamBName || '') !== (state.captaincy?.capt2_team || '');
    if (!dirtyOvers && !dirtyPPS && !dirtyLMS && !dirtySB && !dirtyMB && !dirtyTA && !dirtyTB) return;

    const t = setTimeout(() => {
      api.post(`/scorer/matches/${matchId}/setup`, {
        overs,
        players_per_side: playersSide,
        last_man_stands: lastManStands,
        saanjha_active: saanjha,
        min_bowlers: minBowlers ?? null,
        // Only send a name once the admin has typed something — a blank field
        // must never overwrite an existing name (the server also guards this).
        ...(teamAName && teamAName.trim() ? { team_a_name: teamAName.trim() } : {}),
        ...(teamBName && teamBName.trim() ? { team_b_name: teamBName.trim() } : {}),
      }).catch(e => console.warn('rule save failed:', e?.response?.data?.error || e.message));
    }, 500);
    return () => clearTimeout(t);
  }, [overs, playersSide, lastManStands, saanjha, minBowlers, teamAName, teamBName, state?.match?.id]);

  // Cancel modal state
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState(null);
  const [cancelNotes,  setCancelNotes]  = useState('');

  // Add-member modal (admin-only) — for adding members not in playing list
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  // Conditions modal — for editing no_bowling/no_keeping/leaving_early
  // on any member's availability row
  const [conditionsMember, setConditionsMember] = useState(null);

  // Action-in-flight flag (prevents double-clicks during pick)
  const [busy, setBusy] = useState(false);

  // ── Load gates ─────────────────────────────────────────────
  if (loading && !state) {
    return <div className="ms-root"><div className="ms-page-state">Loading match setup…</div></div>;
  }
  if (error && !state) {
    return (
      <div className="ms-root">
        <div className="ms-page-state ms-error">
          Couldn't load match setup: {error}
          <div><button className="ms-btn-secondary" onClick={refetch}>Retry</button></div>
        </div>
      </div>
    );
  }
  if (!state) return null;

  // ── Captaincy-not-finalized gate ───────────────────────────
  // Require BOTH captains assigned — not merely a captaincy record. A record
  // can exist with null capt ids (e.g. pool seeded but wagon-wheel not run),
  // in which case scoring/squad setup must still be blocked.
  if (!state.captaincy || !state.captaincy.capt1_id || !state.captaincy.capt2_id) {
    return (
      <div className="ms-root">
        <div className="ms-page-state">
          <h2>Captains haven't been finalized yet</h2>
          <p>Run the wagon-wheel selection and finalize captains before squad setup.</p>
          <button className="ms-btn-secondary" onClick={() => navigate('/admin')}>
            Go to admin
          </button>
        </div>
      </div>
    );
  }

  // ── Permission gate ─────────────────────────────────────────
  const cap = state.captaincy;
  // B7/B9 — treat 'scorer' the same as admin for the setup screen:
  // they can set both tosses, save squads, and finalize match config.
  const isAdmin   = ['super_admin', 'admin', 'scorer'].includes(member?.role);
  const isCapt1   = myMemberId && myMemberId === cap.capt1_id;
  const isCapt2   = myMemberId && myMemberId === cap.capt2_id;
  const isCaptain = isCapt1 || isCapt2;
  const myCaptainSide = isCapt1 ? 'A' : (isCapt2 ? 'B' : null);
  const canEdit       = isAdmin || isCaptain;

  // ── Status branches ────────────────────────────────────────
  const status = state.match_status;

  if (status === 'cancelled') {
    return <CancelledView state={state} onBack={() => navigate(-1)} />;
  }
  if (status === 'live' || status === 'completed' || status === 'abandoned') {
    return <LiveOrDoneView state={state} onBack={() => navigate(-1)} matchId={matchId} navigate={navigate} />;
  }

  // ── Pending / setup_done (the real page) ───────────────────
  const squadSize  = playersSide;
  const turn       = whoseTurn(state, squadSize);
  // The Sanjha Billa plays for both sides and is not part of the draft.
  const aSquad     = state.squad.filter(m => m.side === 'A' && !m.is_saanjha);
  const bSquad     = state.squad.filter(m => m.side === 'B' && !m.is_saanjha);
  const saanjhaRow = state.squad.find(m => m.is_saanjha) || null;
  const aFull      = aSquad.length >= squadSize;
  const bFull      = bSquad.length >= squadSize;
  const bothFull   = aFull && bFull;
  // A series fixture has no squad-selection toss: the squads were picked days
  // earlier by the captains and pushed into match_squads. If both sides are
  // already populated there is nothing for the toss to decide, so the step is
  // treated as done and the scorer goes straight to the bat/bowl toss.
  const toss1Done  = !!cap.squad_selection_winner_id
                     || (aSquad.length > 0 && bSquad.length > 0);
  const toss2Done  = !!(cap.winner_id && cap.toss_decision);
  const captainsInSquad = !!aSquad.find(m => m.is_captain) && !!bSquad.find(m => m.is_captain);

  // Unassigned: present members not currently in match_squads
  const squadIds = new Set(state.squad.map(s => s.member_id));
  const unassigned = state.present_members.filter(m => !squadIds.has(m.id));

  // ─── Action handlers ────────────────────────────────────────
  // Admin: bulk-replace squads (the only path that writes captaincy fields).
  // Used to: (a) seed the captains as squad rows on first toss-1 set,
  //          (b) record toss-1 winner, toss-2 winner, decision, team names.
  // Captains never use this — they go through POST /squads/:memberId.
  async function adminBulkReplace(overrides = {}) {
    if (!isAdmin) return;

    const captA = aSquad.find(m => m.is_captain) || (cap.capt1_id ? {
      member_id: cap.capt1_id, side: 'A', is_keeper: false, is_saanjha: false,
      can_bowl: true, batting_order: null, pick_order: 0,
    } : null);
    const captB = bSquad.find(m => m.is_captain) || (cap.capt2_id ? {
      member_id: cap.capt2_id, side: 'B', is_keeper: false, is_saanjha: false,
      can_bowl: true, batting_order: null, pick_order: 0,
    } : null);

    const allMembers = [];
    if (captA) allMembers.push(normalizeMember(captA, 'A', true));
    if (captB) allMembers.push(normalizeMember(captB, 'B', true));
    state.squad
      .filter(m => !m.is_captain)
      .forEach(m => allMembers.push(normalizeMember(m, m.side, false)));

    const body = {
      side_a: { team_name: teamAName || cap.capt1_team || 'Side A', captain_id: cap.capt1_id },
      side_b: { team_name: teamBName || cap.capt2_team || 'Side B', captain_id: cap.capt2_id },
      toss: {
        squad_selection_winner_id: cap.squad_selection_winner_id || null,
        winner_id:   cap.winner_id || null,
        decision:    cap.toss_decision || null,
        squad_pick_order: cap.squad_pick_order || [],
        ...overrides.toss,
      },
      members: allMembers,
    };

    setBusy(true);
    try {
      await api.post(`/scorer/matches/${matchId}/squads`, body);
      await refetch();
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.error || e.message));
    } finally {
      setBusy(false);
    }
  }

  function normalizeMember(m, side, isCaptain) {
    return {
      member_id: m.member_id,
      side,
      is_keeper:    !!m.is_keeper,
      is_saanjha:   !!m.is_saanjha,
      can_bowl:     m.can_bowl !== false,
      batting_order: m.batting_order ?? null,
      pick_order:   isCaptain ? 0 : (m.pick_order ?? null),
    };
  }

  // Admin sets toss-1 winner. Wraps adminBulkReplace.
  async function setToss1Winner(side) {
    const winnerId = side === 'A' ? cap.capt1_id : cap.capt2_id;
    if (!winnerId) {
      alert('Captain not set for that side — finalize captains first.');
      return;
    }
    await adminBulkReplace({ toss: { squad_selection_winner_id: winnerId } });
  }

  async function clearToss1() {
    if (!window.confirm('Clear the squad-selection toss? This will reset all picks.')) return;
    setBusy(true);
    try {
      // Easiest path: bulk-replace with squad_selection_winner_id=null + only captains
      const body = {
        side_a: { team_name: teamAName || cap.capt1_team || 'Side A', captain_id: cap.capt1_id },
        side_b: { team_name: teamBName || cap.capt2_team || 'Side B', captain_id: cap.capt2_id },
        toss: {
          squad_selection_winner_id: null,
          winner_id: cap.winner_id || null,
          decision:  cap.toss_decision || null,
          squad_pick_order: [],
        },
        members: [
          ...(cap.capt1_id ? [{ member_id: cap.capt1_id, side: 'A', pick_order: 0 }] : []),
          ...(cap.capt2_id ? [{ member_id: cap.capt2_id, side: 'B', pick_order: 0 }] : []),
        ],
      };
      await api.post(`/scorer/matches/${matchId}/squads`, body);
      await refetch();
    } catch (e) {
      alert('Reset failed: ' + (e?.response?.data?.error || e.message));
    } finally {
      setBusy(false);
    }
  }

  // Anyone (admin or correct captain) — pick a single member to a side
  async function pickMember(memberId, side) {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/scorer/matches/${matchId}/squads/${memberId}`, { side });
      await refetch();
    } catch (e) {
      const data = e?.response?.data;
      const msg  = (data?.error || e.message || 'Pick failed')
                 + (data?.detail ? ' — ' + data.detail : '');
      alert(msg);
    } finally {
      setBusy(false);
    }
  }

  // Admin/scorer — make a player the Sanjha Billa (plays for both sides), or
  // clear it with null. Needs the "Saanjha billa: Active" rule switched on.
  async function setSaanjhaPlayer(memberId) {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/scorer/matches/${matchId}/saanjha`, { member_id: memberId || null });
      await refetch();
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Sanjha Billa update failed');
    } finally {
      setBusy(false);
    }
  }

  // Anyone (admin or correct-side captain) — send back to unassigned
  async function removeMember(memberId) {
    if (busy) return;
    if (!window.confirm('Send this player back to unassigned?')) return;
    setBusy(true);
    try {
      await api.delete(`/scorer/matches/${matchId}/squads/${memberId}`);
      await refetch();
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Remove failed');
    } finally {
      setBusy(false);
    }
  }

  // Mark / unmark a player as their side's wicketkeeper. The backend enforces
  // one keeper per side (clears others) and rejects members flagged
  // avoids_wicketkeeping, so we just send the toggle.
  async function setKeeper(memberId, makeKeeper) {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/scorer/matches/${matchId}/squads/${memberId}`, { is_keeper: !!makeKeeper });
      await refetch();
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Keeper update failed');
    } finally {
      setBusy(false);
    }
  }

  // Admin sets toss-2 + decision
  async function setToss2(winnerSide, decision) {
    const winnerId = winnerSide === 'A' ? cap.capt1_id : cap.capt2_id;
    await adminBulkReplace({
      toss: {
        squad_selection_winner_id: cap.squad_selection_winner_id,
        winner_id: winnerId,
        decision,
      }
    });
  }

  async function submitFinal() {
    // Same as adminBulkReplace but also commits the local rule values via
    // a separate matches PATCH (overs/players/side aren't in /squads body).
    setBusy(true);
    try {
      // 1. Persist matches.overs and matches.players_per_side
      await api.patch(`/scorer/matches/${matchId}/setup`, {
        overs, players_per_side: playersSide,
        last_man_stands: lastManStands, saanjha: saanjha,
      }).catch(() => { /* admin /setup endpoint may differ — falls back to noop */ });

      // 2. Final bulk replace to flush team names + ensure captaincy is in sync
      await adminBulkReplace();

      // 3. Confirmation: ready to start scoring?
      if (window.confirm(
        'Setup locked.\n\nReady to start scoring? (You can come back to this page anytime.)'
      )) {
        navigate(`/matches/${matchId}/score`);
      }
    } finally {
      setBusy(false);
    }
  }

  async function cancelMatch() {
    if (!cancelReason) { alert('Pick a reason first'); return; }
    setBusy(true);
    try {
      await api.post(`/scorer/matches/${matchId}/cancel`, {
        reason: cancelReason,
        notes:  cancelNotes,
      });
      setCancelOpen(false);
      await refetch();
    } catch (e) {
      alert('Cancel failed: ' + (e?.response?.data?.error || e.message));
    } finally {
      setBusy(false);
    }
  }

  // ─── Render ─────────────────────────────────────────────────
  const captainName = (id) => state.present_members.find(p => p.id === id)?.display_name
                            || id?.slice(0, 8);

  return (
    <div className="ms-root">
      {/* Header */}
      <div className="ms-header">
        <button className="ms-icon-btn" onClick={() => navigate(-1)} aria-label="Back">←</button>
        <div className="ms-title">
          <div className="ms-title-row">
            <span className="ms-ucc-tag">UCC</span>
            <h1>Match Setup</h1>
          </div>
          <div className="ms-subtitle">{fmtMatchDate(state.match?.match_date)}</div>
        </div>
        {isAdmin && (
          <button className="ms-icon-btn" onClick={() => setCancelOpen(true)} aria-label="Cancel match">×</button>
        )}
      </div>

      {/* Status banner */}
      {!toss1Done && (
        <div className="ms-status-banner ms-pending">
          <span>{isAdmin ? 'Step 1 — Set the squad-selection toss winner' : 'Waiting for admin to set squad-selection toss'}</span>
          <span className="ms-collab-pill"><span className="ms-live-dot" />Live</span>
        </div>
      )}
      {toss1Done && !bothFull && (
        <div className="ms-status-banner ms-pending">
          <span>
            {turn
              ? `${captainName(turn === 'A' ? cap.capt1_id : cap.capt2_id)} on the clock — pick #${aSquad.filter(m=>!m.is_captain).length + bSquad.filter(m=>!m.is_captain).length + 1}`
              : 'Squad picking complete — set bat/bowl toss'}
          </span>
          <span className="ms-collab-pill"><span className="ms-live-dot" />Live</span>
        </div>
      )}
      {bothFull && !toss2Done && (
        <div className="ms-status-banner ms-pending">
          <span>{isAdmin ? 'Step 3 — Set bat/bowl toss' : 'Waiting for admin to set bat/bowl toss'}</span>
        </div>
      )}
      {bothFull && toss2Done && (
        <div className="ms-status-banner ms-setup-done">
          <span>✓ Setup complete · ready to start innings 1</span>
        </div>
      )}

      {/* Captains banner */}
      <div className="ms-captains-banner">
        <div className="ms-captains-row">
          <div className={`ms-captain-block ms-side-a ${turn === 'A' ? 'ms-on-clock' : ''}`}>
            <div className="ms-captain-avatar">{(captainName(cap.capt1_id) || '?').split(' ').map(s => s[0]).slice(0,2).join('')}</div>
            <div className="ms-captain-info">
              <div className="ms-label">
                <span>Captain A</span>
                {turn === 'A' && <span className="ms-onclock-pill">PICKING</span>}
              </div>
              <div className="ms-name">{captainName(cap.capt1_id)}</div>
            </div>
          </div>
          <div className="ms-captain-vs">VS</div>
          <div className={`ms-captain-block ms-side-b ${turn === 'B' ? 'ms-on-clock' : ''}`}>
            <div className="ms-captain-avatar">{(captainName(cap.capt2_id) || '?').split(' ').map(s => s[0]).slice(0,2).join('')}</div>
            <div className="ms-captain-info">
              <div className="ms-label">
                {turn === 'B' && <span className="ms-onclock-pill">PICKING</span>}
                <span>Captain B</span>
              </div>
              <div className="ms-name">{captainName(cap.capt2_id)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky compact roster bar */}
      <div className="ms-roster-bar">
        <RosterPill side="A" name={teamAName} squad={aSquad} squadSize={squadSize} present={state.present_members} strengthsByMember={strengthsByMember} />
        <RosterPill side="B" name={teamBName} squad={bSquad} squadSize={squadSize} present={state.present_members} strengthsByMember={strengthsByMember} />
      </div>

      {/* Content */}
      <div className="ms-content">
        {/* Step 1 — Squad-selection toss */}
        <div className={`ms-section-title ${!toss1Done ? 'ms-active' : 'ms-done'}`}>
          <span><span className="ms-step-num">1</span>Squad-selection toss</span>
        </div>
        {!toss1Done && (
          <div className="ms-toss-panel">
            {isAdmin ? (
              <>
                <div className="ms-toss-question">Who won the toss for picking first?</div>
                <div className="ms-toss-options">
                  <button className="ms-toss-option-btn" disabled={busy} onClick={() => setToss1Winner('A')}>
                    <div className="ms-opt-name">{captainName(cap.capt1_id)}</div>
                    <div className="ms-opt-sub">Side A · {teamAName || 'LeafBerry Ads'}</div>
                  </button>
                  <button className="ms-toss-option-btn" disabled={busy} onClick={() => setToss1Winner('B')}>
                    <div className="ms-opt-name">{captainName(cap.capt2_id)}</div>
                    <div className="ms-opt-sub">Side B · {teamBName || 'Lead Ads'}</div>
                  </button>
                </div>
              </>
            ) : (
              <div style={{ color: 'var(--ms-gray-500)', fontSize: 13, textAlign: 'center', padding: 8 }}>
                Waiting for admin/scorer to record the toss winner…
              </div>
            )}
          </div>
        )}
        {toss1Done && (
          <div className="ms-toss-panel ms-completed">
            <div className="ms-toss-result-row">
              <div className="ms-toss-icon">✓</div>
              <div>
                <strong>{captainName(cap.squad_selection_winner_id)}</strong> won the toss · picks first
              </div>
              {isAdmin && (
                <button className="ms-toss-edit" disabled={busy} onClick={clearToss1}>Edit</button>
              )}
            </div>
          </div>
        )}

        {/* Step 2 — Sides & squads */}
        <div className={`ms-section-title ${toss1Done && !bothFull ? 'ms-active' : (bothFull ? 'ms-done' : '')}`}>
          <span><span className="ms-step-num">2</span>Sides &amp; squads</span>
        </div>
        {!toss1Done ? (
          <div className="ms-locked-overlay">
            🔒 Set the squad-selection toss first
          </div>
        ) : (
          <>
            <SidesGrid
              squad={state.squad.filter(m => !m.is_saanjha)}
              captaincy={cap}
              presentMembers={state.present_members}
              strengthsByMember={strengthsByMember}
              squadSize={squadSize}
              turn={turn}
              canEditMeta={isAdmin}
              isAdmin={isAdmin}
              myCaptainSide={myCaptainSide}
              teamAName={teamAName}
              teamBName={teamBName}
              onTeamAChange={setTeamAName}
              onTeamBChange={setTeamBName}
              onRemove={removeMember}
              onSetKeeper={setKeeper}
              onEditConditions={isAdmin ? setConditionsMember : null}
              floaterPriorities={state.floater_priority_by_member_id || {}}
              capHolders={{
                orange_cap_member_id: state.orange_cap_member_id,
                purple_cap_member_id: state.purple_cap_member_id,
                green_cap_member_id:  state.green_cap_member_id,
                blue_cap_member_id:   state.blue_cap_member_id,
              }}
            />

            <div className="ms-unassigned-section">
              {turn && (
                <div className={`ms-turn-banner ${turn === 'B' ? 'ms-side-b' : ''}`}>
                  <div className="ms-clock-icon">⏱</div>
                  <div>
                    <strong>{captainName(turn === 'A' ? cap.capt1_id : cap.capt2_id)}</strong>
                    {' '}on the clock <span style={{ opacity: .8 }}>· pick #{aSquad.filter(m=>!m.is_captain).length + bSquad.filter(m=>!m.is_captain).length + 1}</span>
                  </div>
                  <div className="ms-turn-meta">
                    Side {turn} picks · Side {turn === 'A' ? 'B' : 'A'} next
                  </div>
                </div>
              )}
              <div className="ms-unassigned-controls">
                <div className="ms-unassigned-meta">
                  <strong>{state.present_members.length}</strong> present · <strong>{unassigned.length}</strong> unassigned
                </div>
                {isAdmin && (
                  <button
                    className="ms-add-member-link"
                    onClick={() => setAddMemberOpen(true)}
                    title="Add a member who isn't in the playing list"
                  >
                    + Add member
                  </button>
                )}
              </div>
              {(saanjha || saanjhaRow) && (
                <SaanjhaPanel
                  saanjhaRow={saanjhaRow}
                  candidates={unassigned.filter(m => m.id !== cap.capt1_id && m.id !== cap.capt2_id)}
                  presentMembers={state.present_members}
                  isAdmin={isAdmin}
                  busy={busy}
                  saanjhaOn={saanjha}
                  onSet={setSaanjhaPlayer}
                />
              )}
              <UnassignedList
                members={unassigned}
                strengthsByMember={strengthsByMember}
                turn={turn}
                canEdit={canEdit}
                myCaptainSide={myCaptainSide}
                isAdmin={isAdmin}
                onPick={pickMember}
                onEditConditions={isAdmin ? setConditionsMember : null}
                floaterPriorities={state.floater_priority_by_member_id || {}}
                capHolders={{
                  orange_cap_member_id: state.orange_cap_member_id,
                  purple_cap_member_id: state.purple_cap_member_id,
                  green_cap_member_id:  state.green_cap_member_id,
                  blue_cap_member_id:   state.blue_cap_member_id,
                }}
              />
            </div>
          </>
        )}

        {/* Step 3 — Bat/bowl toss */}
        <div className={`ms-section-title ${bothFull && !toss2Done ? 'ms-active' : (toss2Done ? 'ms-done' : '')}`}>
          <span><span className="ms-step-num">3</span>Bat / bowl toss</span>
        </div>
        {!bothFull ? (
          <div className="ms-locked-overlay">
            🔒 Both squads must be picked first
          </div>
        ) : !toss2Done ? (
          <div className="ms-toss-panel">
            {isAdmin ? (
              <Toss2Picker
                cap={cap}
                teamAName={teamAName}
                teamBName={teamBName}
                captainName={captainName}
                onSet={setToss2}
                busy={busy}
              />
            ) : (
              <div style={{ color: 'var(--ms-gray-500)', fontSize: 13, textAlign: 'center', padding: 8 }}>
                Waiting for admin/scorer to record the bat/bowl toss…
              </div>
            )}
          </div>
        ) : (
          <div className="ms-toss-panel ms-completed">
            <div className="ms-toss-result-row">
              <div className="ms-toss-icon">✓</div>
              <div>
                <strong>{captainName(cap.winner_id)}</strong> won · chose to <strong>{cap.toss_decision}</strong>
              </div>
            </div>
          </div>
        )}

        {/* Step 4 — Match rules (admin only) */}
        {isAdmin && (
          <>
            <div className="ms-section-title">
              <span><span className="ms-step-num">4</span>Match rules</span>
            </div>
            <RulesPanel
              overs={overs} setOvers={setOvers}
              playersSide={playersSide} setPlayersSide={setPlayersSide}
              lastManStands={lastManStands} setLMS={setLMS}
              saanjha={saanjha} setSaanjha={setSaanjha}
              minBowlers={minBowlers} setMinBowlers={setMinBowlers}
            />
          </>
        )}
      </div>

      {/* Bottom bar — admin submit (only when ready). The "Save & lock setup"
          flow normally takes you to scoring after confirmation. The secondary
          "▶ Open scoring console" button lets you jump there directly if
          setup is already locked but you closed the page (or the post-lock
          dialog was dismissed). */}
      {isAdmin && bothFull && toss2Done && (
        <div className="ms-bottom-bar" style={{ display: 'flex', gap: 8 }}>
          <button
            className="ms-submit-btn ms-green"
            disabled={busy}
            onClick={submitFinal}
            style={{ flex: 1 }}
          >
            ▶ Save &amp; lock setup
          </button>
          {status === 'setup_done' && (
            <button
              className="ms-btn-secondary"
              onClick={() => navigate(`/matches/${matchId}/score`)}
              style={{ flex: 1 }}
            >
              Open scoring →
            </button>
          )}
        </div>
      )}

      {/* Add-member modal (admin only) */}
      {addMemberOpen && (
        <AddMemberModal
          matchId={matchId}
          onClose={() => setAddMemberOpen(false)}
          onAdded={refetch}
        />
      )}

      {/* Conditions modal — opens when any name is tapped (admin only for now) */}
      {conditionsMember && (
        <ConditionsModal
          matchId={matchId}
          member={conditionsMember}
          canEdit={isAdmin}
          onClose={() => setConditionsMember(null)}
          onSaved={refetch}
        />
      )}

      {/* Cancel modal */}
      {cancelOpen && (
        <div className="ms-modal-overlay" onClick={e => e.target === e.currentTarget && setCancelOpen(false)}>
          <div className="ms-modal">
            <div className="ms-modal-head">
              <h3>Cancel match?</h3>
              <button className="ms-icon-btn-sm" onClick={() => setCancelOpen(false)}>×</button>
            </div>
            <div className="ms-modal-body">
              <p style={{ fontSize: 13, color: 'var(--ms-gray-700)', lineHeight: 1.5 }}>
                Pick a reason. Any partial scorecard will be preserved.
              </p>
              <div className="ms-reason-chips">
                {['rain','low_attendance','darkness','pitch_unsafe','emergency','other'].map(r => (
                  <button
                    key={r}
                    className={`ms-reason-chip ${cancelReason === r ? 'ms-active' : ''}`}
                    onClick={() => setCancelReason(r)}
                  >
                    {r.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
              <textarea
                placeholder="Notes (optional)"
                value={cancelNotes}
                onChange={e => setCancelNotes(e.target.value)}
              />
            </div>
            <div className="ms-modal-foot">
              <button className="ms-btn-cancel-x" onClick={() => setCancelOpen(false)}>Keep match</button>
              <button className="ms-btn-confirm" onClick={cancelMatch} disabled={busy || !cancelReason}>Cancel match</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────
// ─── Sticky-bar composition counter ──────────────────────────
// Same look as the Composition strip we briefly experimented with on the
// side card: one row per role, with a colored role chip on the left and
// per-player tier pips on the right.
//
//   BAT  ×4    [T1] [T1] [T2] [T3]
//   BOWL ×1    [T1]
//   AR   ×8    [T1] [T1] [T1] [T1] [T1] [T2] [T2] [T3]
//   WK   ×1    [T2]
//
// Sized down vs the side-card version so it fits in the sticky roster bar
// on phone. Hidden until the first pick is made.
// ─── Composition buckets — per-discipline tiers ───────────────
//
// Each row is built independently. A player can appear in multiple rows
// (a real-world all-rounder shows up in BAT, BOWL, AR, and FLD).
//
//   BAT  — players with batting innings > 0 this season
//          tier = batting_tier            label "BT1/BT2/BT3"
//   BOWL — players with bowling innings > 0 this season
//          tier = bowling_tier            label "BL1/BL2/BL3"
//   AR   — players whose 70/30 split makes them all-rounders
//          tier = overall_tier            label "AR1/AR2/AR3"
//   WK   — players meeting the keeper threshold OR keeping today
//          tier = overall_tier            label "T1/T2/T3"
//   FLD  — entire squad (everyone fields)
//          tier = fielding_tier           label "F1/F2/F3"
//
// Bowler floor — same threshold the captions use for "Bowl T#":
//   bowling_innings >= 3 AND bowling_innings/attended >= 0.25
// This keeps BAT-only players who've never bowled out of the Bowler row.
//
// Empty rows are filtered out by the render layer.
// ─── Composition buckets — per-discipline RANKS ───────────────
//
// Ranks are computed server-side over today's playing pool only, in
// /scorer/matches/:id/member-strengths. Lower rank = better:
//   batting_rank   — by total runs this season
//   bowling_rank   — by total wickets this season
//   ar_rank        — by total UCC points (genuine ARs only, 70/30 split)
//   keeper_rank    — by stumpings + caught_behind this season
//   fielding_rank  — by (catches − caught_behind + run_outs) this season
//
// Each rank is null if the player has 0 of that metric, except for
// Fielder where every player on the squad shows up (rank null = no
// outfielding dismissals yet, rendered as "—").
//
// A player can appear in multiple rows — that's a real all-rounder
// or a keeper-batter.
function rosterCompositionBuckets(squad, strengthsByMember) {
  const buckets = { BAT: [], BOWL: [], AR: [], WK: [], FLD: [] };

  squad.forEach(r => {
    const s = strengthsByMember && strengthsByMember.get(r.member_id);

    // ─── Fielder row: every player on the squad ──────────────
    buckets.FLD.push(s?.fielding_rank || null);

    if (!s) return;

    // ─── Batter row: must have batting_rank (server gates this) ─
    if (s.batting_rank) buckets.BAT.push(s.batting_rank);

    // ─── Bowler row: must have at least one wicket this season ─
    if (s.bowling_rank) buckets.BOWL.push(s.bowling_rank);

    // ─── All-rounder row: server emits ar_rank only for ARs ──
    if (s.ar_rank) buckets.AR.push(s.ar_rank);

    // ─── WK row: season-keeper OR today's keeper ─────────────
    const kInn = Number(s.keeper_innings) || 0;
    const att  = Math.max(
      Number(s.matches_attended) || 0,
      Number(s.batting_innings)  || 0,
      Number(s.bowling_innings)  || 0,
      kInn,
    );
    const isSeasonKeeper = kInn >= 3 && att > 0 && (kInn / att) >= 0.25;
    if (isSeasonKeeper || r.is_keeper) {
      buckets.WK.push(s.keeper_rank || null);
    }
  });

  // Sort ascending so #1 renders leftmost. null sinks to the end.
  Object.keys(buckets).forEach(k => {
    buckets[k].sort((a, b) => (a ?? 9999) - (b ?? 9999));
  });
  return buckets;
}

function RosterPill({ side, name, squad, squadSize, present, strengthsByMember }) {
  const sideClass = side === 'A' ? 'ms-side-a' : 'ms-side-b';
  const presentByMemberId = new Map();
  present.forEach(p => presentByMemberId.set(p.id, p));
  const nameLabel = makeNameLabeler(present);
  const sorted = [...squad].sort((a, b) => {
    if (a.is_captain && !b.is_captain) return -1;
    if (b.is_captain && !a.is_captain) return  1;
    return (a.pick_order || 0) - (b.pick_order || 0);
  });

  const buckets = rosterCompositionBuckets(squad, strengthsByMember);

  // Pip label is just "#N" — the row label tells you which discipline.
  const ROW_META = {
    BAT:  { label: 'Batter',      className: 'ms-comp-role-bat'  },
    BOWL: { label: 'Bowler',      className: 'ms-comp-role-bowl' },
    AR:   { label: 'All-rounder', className: 'ms-comp-role-ar'   },
    WK:   { label: 'Keeper',      className: 'ms-comp-role-wk'   },
    FLD:  { label: 'Fielder',     className: 'ms-comp-role-fld'  },
  };
  const ROW_ORDER = ['BAT', 'BOWL', 'AR', 'WK', 'FLD'];
  const visibleRows = ROW_ORDER.filter(k => buckets[k].length > 0);
  const showCounter = squad.length > 0;

  return (
    <div className={`ms-roster-pill ${sideClass}`}>
      <div className="ms-pill-row1">
        <div className="ms-badge-letter">{side}</div>
        <div className="ms-team-name">{name || `Side ${side}`}</div>
        <div className="ms-count">{squad.length}/{squadSize}</div>
      </div>
      <div className="ms-roster-chips">
        {sorted.length === 0
          ? <span className="ms-roster-empty">no players yet</span>
          : sorted.map(r => {
              const m = presentByMemberId.get(r.member_id);
              const cls = r.is_captain ? 'ms-roster-chip ms-cap' : 'ms-roster-chip';
              return (
                <span className={cls} key={r.member_id}>
                  {nameLabel(m)}
                  <span className="ms-role-mini">· {r.is_captain ? 'CAP' : '#' + (r.pick_order || '?')}</span>
                </span>
              );
            })}
      </div>
      {showCounter && (
        <CompositionStrip rows={ROW_ORDER.filter(k => buckets[k].length > 0)} buckets={buckets} ROW_META={ROW_META} side={side} />
      )}
    </div>
  );
}

// Collapsible composition — rendered below the chip list inside the sticky
// roster pill. Default collapsed; one-tap toggle expands. Smooth on iPhone
// Safari + Android Chrome (uses max-height for transition, not display:none
// so the toggle animation feels right).
function CompositionStrip({ rows, buckets, ROW_META, side }) {
  const [open, setOpen] = useState(false);
  if (rows.length === 0) return null;
  return (
    <div className={`ms-composition-wrap ${open ? 'ms-comp-open' : 'ms-comp-closed'}`}>
      <button
        type="button"
        className="ms-comp-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={`comp-body-${side}`}
      >
        {open ? 'Hide breakdown' : 'Show breakdown'}
        <span className="ms-comp-toggle-caret" aria-hidden="true">{open ? '▴' : '▾'}</span>
      </button>
      <div
        id={`comp-body-${side}`}
        className="ms-composition ms-comp-in-sticky"
        aria-label="Squad composition"
        aria-hidden={!open}
      >
        {rows.map(rowKey => {
          const meta  = ROW_META[rowKey];
          const ranks = buckets[rowKey];
          return (
            <div className="ms-comp-row" key={rowKey}>
              <span className={`ms-comp-role ${meta.className}`}>
                {meta.label}
                <span className="ms-comp-role-count">×{ranks.length}</span>
              </span>
              <span className="ms-comp-pips">
                {ranks.map((rank, i) => {
                  const isTop3 = rank && rank <= 3;
                  const cls = !rank   ? 'ms-comp-pip ms-pip-rank-na'
                            : isTop3  ? 'ms-comp-pip ms-pip-rank-top'
                            :           'ms-comp-pip ms-pip-rank';
                  const label = rank ? `#${rank}` : '\u2014';
                  const title = rank
                    ? `Rank ${rank} in ${meta.label.toLowerCase()} among today's playing pool`
                    : `${meta.label} \u2014 no season data yet`;
                  return (
                    <span className={cls} key={i} title={title}>{label}</span>
                  );
                })}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Toss2Picker({ cap, teamAName, teamBName, captainName, onSet, busy }) {
  const [winner, setWinner] = useState(null);
  const [decision, setDecision] = useState(null);

  function commit() {
    if (!winner || !decision) return;
    onSet(winner, decision);
  }

  return (
    <>
      <div className="ms-toss-question">Who won the toss?</div>
      <div className="ms-toss-options" style={{ marginBottom: 12 }}>
        <button className={`ms-toss-option-btn ${winner === 'A' ? 'ms-selected' : ''}`} onClick={() => setWinner('A')}>
          <div className="ms-opt-name">{captainName(cap.capt1_id)}</div>
          <div className="ms-opt-sub">Side A</div>
        </button>
        <button className={`ms-toss-option-btn ${winner === 'B' ? 'ms-selected' : ''}`} onClick={() => setWinner('B')}>
          <div className="ms-opt-name">{captainName(cap.capt2_id)}</div>
          <div className="ms-opt-sub">Side B</div>
        </button>
      </div>
      <div className="ms-toss-question">Decision</div>
      <div className="ms-toss-options" style={{ marginBottom: 12 }}>
        <button className={`ms-toss-option-btn ${decision === 'bat' ? 'ms-selected' : ''}`} onClick={() => setDecision('bat')}>
          <div className="ms-opt-name">Bat first</div>
        </button>
        <button className={`ms-toss-option-btn ${decision === 'bowl' ? 'ms-selected' : ''}`} onClick={() => setDecision('bowl')}>
          <div className="ms-opt-name">Bowl first</div>
        </button>
      </div>
      <button
        className="ms-submit-btn"
        disabled={busy || !winner || !decision}
        onClick={commit}
      >
        Save bat/bowl toss
      </button>
    </>
  );
}

function RulesPanel({ overs, setOvers, playersSide, setPlayersSide,
                     lastManStands, setLMS, saanjha, setSaanjha, minBowlers, setMinBowlers }) {
  const oversChips = [20, 25, 30];
  // B9 — extended from [11,12,13] to [10,11,12,13,14] per match-day reality
  const playersChips = [10, 11, 12, 13, 14];
  const isOversCustom   = !oversChips.includes(overs);
  const isPlayersCustom = !playersChips.includes(playersSide);

  return (
    <div className="ms-config-card">
      <div className="ms-config-block" style={{ marginBottom: 12 }}>
        <div className="ms-config-label">Overs</div>
        <div className="ms-chip-picker">
          {oversChips.map(v => (
            <button
              key={v}
              className={`ms-chip ${!isOversCustom && overs === v ? 'ms-active' : ''}`}
              onClick={() => setOvers(v)}
            >{v}</button>
          ))}
          <label className={`ms-chip ms-chip-custom ${isOversCustom ? 'ms-active' : ''}`}>
            <input
              type="number" placeholder="Custom" min={1} max={50}
              inputMode="numeric"
              value={isOversCustom ? overs : ''}
              onChange={e => setOvers(parseInt(e.target.value, 10) || 0)}
            />
          </label>
        </div>
      </div>

      <div className="ms-config-block" style={{ marginBottom: 12 }}>
        <div className="ms-config-label">Players / side</div>
        <div className="ms-chip-picker">
          {playersChips.map(v => (
            <button
              key={v}
              className={`ms-chip ${!isPlayersCustom && playersSide === v ? 'ms-active' : ''}`}
              onClick={() => setPlayersSide(v)}
            >{v}</button>
          ))}
          <label className={`ms-chip ms-chip-custom ${isPlayersCustom ? 'ms-active' : ''}`}>
            <input
              type="number" placeholder="Custom" min={6} max={15}
              inputMode="numeric"
              value={isPlayersCustom ? playersSide : ''}
              onChange={e => setPlayersSide(parseInt(e.target.value, 10) || 0)}
            />
          </label>
        </div>
      </div>

      <div className="ms-config-row">
        <div className="ms-config-block">
          <div className="ms-config-label">Last man stands</div>
          <div className="ms-seg-toggle">
            <button className={lastManStands ? 'ms-active' : ''} onClick={() => setLMS(true)}>Allowed</button>
            <button className={!lastManStands ? 'ms-active' : ''} onClick={() => setLMS(false)}>Not allowed</button>
          </div>
        </div>
        <div className="ms-config-block">
          <div className="ms-config-label">Saanjha billa</div>
          <div className="ms-seg-toggle">
            <button className={saanjha ? 'ms-active' : ''} onClick={() => setSaanjha(true)}>Active</button>
            <button className={!saanjha ? 'ms-active' : ''} onClick={() => setSaanjha(false)}>None</button>
          </div>
        </div>
      </div>

      <div className="ms-config-block" style={{ marginTop: 12 }}>
        <div className="ms-config-label">Minimum bowlers</div>
        <div className="ms-chip-picker">
          {[[null, 'No limit'], [5, '5'], [6, '6']].map(([v, l]) => (
            <button key={l} className={`ms-chip ${(minBowlers ?? null) === v ? 'ms-active' : ''}`} onClick={() => setMinBowlers(v)}>{l}</button>
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ms-gray-500, #6B7280)', marginTop: 4 }}>
          {minBowlers
            ? `Each bowler can bowl at most ${Math.ceil((overs || 0) / minBowlers)} overs (${overs} ÷ ${minBowlers}, rounded up). The scorer can't pick a bowler who has used them up.`
            : 'Any bowler can bowl any number of overs (regular UCC rules).'}
        </div>
      </div>
    </div>
  );
}

// ─── Sanjha Billa ───────────────────────────────────────────
// With an odd number of players, one plays for BOTH sides: bats, bowls and
// keeps for whichever captain uses him, fields half of each innings. Suggested:
// the present player with the fewest UCC points this season.
function SaanjhaPanel({ saanjhaRow, candidates, presentMembers, isAdmin, busy, saanjhaOn, onSet }) {
  const [choice, setChoice] = useState('');
  const nameOf = (id) => presentMembers.find(p => p.id === id)?.display_name || 'Player';
  const ptsOf  = (id) => presentMembers.find(p => p.id === id)?.season_pts;
  const fmtPts = (v) => (v == null ? '' : `${Math.round(v)} pts`);
  const sorted = [...candidates].sort((a, b) => (a.season_pts ?? 0) - (b.season_pts ?? 0)
                                             || a.display_name.localeCompare(b.display_name));
  const suggested = sorted[0] || null;
  const box = { border: '1.5px solid #C9940F', background: '#FFFDF0', borderRadius: 10, padding: '10px 12px', margin: '8px 0 12px' };
  const title = { fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, color: '#1E3A6E', fontSize: '1rem' };

  if (saanjhaRow) {
    return (
      <div style={box}>
        <div style={title}>🤝 Sanjha Billa: {nameOf(saanjhaRow.member_id)}</div>
        <div style={{ fontSize: 12, color: '#6B7280', margin: '2px 0 8px' }}>
          Plays for both sides: bats, bowls and keeps for either captain, fields half of each innings. {fmtPts(ptsOf(saanjhaRow.member_id))}
        </div>
        {isAdmin && (
          <button className="ms-toss-edit" disabled={busy} onClick={() => onSet(null)}>Remove</button>
        )}
      </div>
    );
  }
  if (!isAdmin || !saanjhaOn) return null;
  return (
    <div style={box}>
      <div style={title}>🤝 Sanjha Billa</div>
      {candidates.length === 0 ? (
        <div style={{ fontSize: 12, color: '#6B7280' }}>Everyone is picked. Send a player back to unassigned to make him Sanjha Billa.</div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: '#6B7280', margin: '2px 0 8px' }}>
            Fewest points this season among the players not yet picked:
            {' '}<strong style={{ color: '#111827' }}>{suggested.display_name}</strong> ({fmtPts(suggested.season_pts)})
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="ms-submit-btn" style={{ padding: '6px 12px' }} disabled={busy}
                    onClick={() => onSet(suggested.id)}>
              Make {suggested.display_name} Sanjha Billa
            </button>
            <select value={choice} onChange={e => setChoice(e.target.value)} style={{ padding: 6, borderRadius: 8 }}>
              <option value="">or choose someone else…</option>
              {sorted.map(m => <option key={m.id} value={m.id}>{m.display_name} · {fmtPts(m.season_pts)}</option>)}
            </select>
            {choice && (
              <button className="ms-toss-edit" disabled={busy} onClick={() => { onSet(choice); setChoice(''); }}>Set</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function CancelledView({ state, onBack }) {
  return (
    <div className="ms-root">
      <div className="ms-header">
        <button className="ms-icon-btn" onClick={onBack}>←</button>
        <div className="ms-title">
          <div className="ms-title-row">
            <span className="ms-ucc-tag">UCC</span>
            <h1>Match cancelled</h1>
          </div>
          <div className="ms-subtitle">{fmtMatchDate(state.match?.match_date)}</div>
        </div>
      </div>
      <div className="ms-content">
        <div className="ms-summary-card">
          <h4>Cancellation</h4>
          <div className="ms-summary-row"><span className="ms-lbl-sm">Status</span><strong style={{ color: 'var(--ms-red)' }}>Cancelled</strong></div>
          <div className="ms-summary-row"><span className="ms-lbl-sm">Reason</span><strong>{state.match?.cancel_reason || '—'}</strong></div>
        </div>
      </div>
    </div>
  );
}

function LiveOrDoneView({ state, onBack, matchId, navigate }) {
  const isLive = state.match_status === 'live';
  const { member } = useAuth();
  const isSuperAdmin = member?.role === 'super_admin';
  const [resetBusy, setResetBusy] = useState(false);

  async function handleReset() {
    const ok = window.confirm(
      'Reset this match?\n\n' +
      'This permanently deletes ALL scoring for this match (every ball, the ' +
      'innings, and derived figures) AND the squads, returning the match to ' +
      'team selection so you can re-pick the teams.\n\n' +
      'Captains, toss, and match settings are kept.\n\n' +
      'This cannot be undone.'
    );
    if (!ok) return;
    try {
      setResetBusy(true);
      await api.post(`/scorer/matches/${matchId}/reset-scoring`, { keep_squads: false });
      // Force a clean reload so the page re-renders in the editable draft stage.
      window.location.reload();
    } catch (e) {
      setResetBusy(false);
      alert(e?.response?.data?.error || e?.message || 'Reset failed');
    }
  }

  // Re-reads every ball and rebuilds batting/bowling/fielding figures, the
  // result and UCC points. Safe to repeat — use after a data correction.
  async function handleRebuild() {
    if (!window.confirm('Rebuild this match\'s figures, result and points from the balls?')) return;
    try {
      setResetBusy(true);
      await api.post(`/scorer/matches/${matchId}/derive`);
      await api.post(`/scorer/matches/${matchId}/result-from-play`).catch(() => {});
      alert('Done — figures, result and points rebuilt.');
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || 'Rebuild failed');
    } finally {
      setResetBusy(false);
    }
  }

  async function handleResetScoringOnly() {
    const ok = window.confirm(
      'Reset scoring only?\n\n' +
      'This permanently deletes ALL scoring for this match (every ball, the ' +
      'innings, derived figures and points) but KEEPS the teams/squads.\n\n' +
      'The match returns to pre-innings, so you can pick openers and score fresh ' +
      'without re-drafting the teams.\n\n' +
      'This cannot be undone.'
    );
    if (!ok) return;
    try {
      setResetBusy(true);
      await api.post(`/scorer/matches/${matchId}/reset-scoring`, { keep_squads: true });
      window.location.reload();
    } catch (e) {
      setResetBusy(false);
      alert(e?.response?.data?.error || e?.message || 'Reset failed');
    }
  }

  return (
    <div className="ms-root">
      <div className="ms-header">
        <button className="ms-icon-btn" onClick={onBack}>←</button>
        <div className="ms-title">
          <div className="ms-title-row">
            <span className="ms-ucc-tag">UCC</span>
            <h1>Match {state.match_status}</h1>
          </div>
          <div className="ms-subtitle">{fmtMatchDate(state.match?.match_date)}</div>
        </div>
      </div>
      <div className="ms-content">
        <div className="ms-summary-card">
          <h4>Status</h4>
          <div className="ms-summary-row">
            <span className="ms-lbl-sm">State</span>
            <strong>{state.match_status}</strong>
          </div>
        </div>
        {isLive && matchId && (
          <button
            className="ms-submit-btn ms-green"
            style={{ width: '100%', marginTop: 12 }}
            onClick={() => navigate(`/matches/${matchId}/score`)}
          >
            ▶ Open scoring console
          </button>
        )}
        {state.match_status === 'completed' && matchId && (
          <button
            className="ms-btn-secondary"
            style={{ width: '100%', marginTop: 12 }}
            onClick={() => navigate(`/scorecards/${matchId}`)}
          >
            View scorecard
          </button>
        )}
        {isSuperAdmin && matchId && (
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #eee' }}>
            <div style={{ fontSize: 12, color: '#b91c1c', marginBottom: 6, fontWeight: 600 }}>
              Super-admin
            </div>
            <button
              className="ms-btn-secondary"
              style={{ width: '100%', marginBottom: 6 }}
              disabled={resetBusy}
              onClick={handleRebuild}
            >
              {resetBusy ? 'Working…' : '🔄 Rebuild figures & points from the balls'}
            </button>
            <button
              className="ms-btn-secondary"
              style={{ width: '100%', borderColor: '#b45309', color: '#b45309', marginBottom: 6 }}
              disabled={resetBusy}
              onClick={handleResetScoringOnly}
            >
              {resetBusy ? 'Resetting…' : '↺ Reset scoring only — keep teams'}
            </button>
            <div style={{ fontSize: 11, color: 'var(--sc-text-faint, #888)', marginBottom: 16, lineHeight: 1.4 }}>
              Wipes all balls, innings, figures and points but KEEPS the squads.
              Returns to pre-innings so you can re-pick openers and score fresh.
            </div>
            <button
              className="ms-btn-secondary"
              style={{ width: '100%', borderColor: '#b91c1c', color: '#b91c1c' }}
              disabled={resetBusy}
              onClick={handleReset}
            >
              {resetBusy ? 'Resetting…' : '↺ Reset match — wipe scoring & re-pick teams'}
            </button>
            <div style={{ fontSize: 11, color: 'var(--sc-text-faint, #888)', marginTop: 6, lineHeight: 1.4 }}>
              Deletes all scoring AND squads for this match and returns it to team
              selection. Captains, toss, and settings are kept. Cannot be undone.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
